/**
 * FilterPanel — sidebar with origin/pool/active/verdict/folder filters.
 * Includes the co-located FolderConsentButton (only used here).
 *
 * Extracted from BrandAssetGallery.jsx during May 2026 refactor.
 */
import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Search as SearchIcon,
  Filter as FilterIcon,
  Trash2,
  X,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  API, ORIGINS, POOLS, VERDICTS, PUBLICATION_STATUSES,
  authedFetch, shortFolder,
} from "./utils";

function FolderConsentButton({ workspaceId, folder, onChange }) {
  const [busy, setBusy] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(null); // null | 'verified' | 'unverified' | 'unknown'
  const [reason, setReason] = useState("");

  const reset = () => {
    setConfirmStatus(null);
    setReason("");
    setBusy(false);
  };

  const submit = async () => {
    if (!confirmStatus) return;
    if ((reason || "").trim().length < 4) {
      toast.error("Add a reason (min 4 chars) — required for the audit trail.");
      return;
    }
    setBusy(true);
    try {
      const r = await authedFetch(
        `${API}/v2/workspaces/${workspaceId}/brand/folders/consent`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            source_folder: folder,
            consent_status: confirmStatus,
            reason: reason.trim(),
          }),
        }
      );
      if (!r.ok) {
        const errText = await r.text();
        throw new Error(errText || `HTTP ${r.status}`);
      }
      const data = await r.json();
      toast.success(
        `${data.assets_updated} assets in this folder marked ${confirmStatus}. ` +
        `${data.verdicts_rederived} verdicts re-derived.`
      );
      reset();
      if (onChange) onChange();
    } catch (e) {
      toast.error(`Failed: ${e.message}`);
      setBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="text-[10px] h-6 px-1.5"
            disabled={busy}
            data-testid="gallery-folder-consent-trigger"
          >
            <ShieldCheck className="h-3 w-3 mr-1" />
            Consent
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="text-xs">
          <DropdownMenuItem
            onClick={() => setConfirmStatus("verified")}
            data-testid="gallery-folder-consent-verified"
          >
            ✓ Mark consent verified (every face has consent)
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setConfirmStatus("unverified")}
            data-testid="gallery-folder-consent-unverified"
          >
            ✗ Mark consent unverified
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setConfirmStatus("unknown")}
            data-testid="gallery-folder-consent-unknown"
          >
            ? Reset to unknown
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!confirmStatus} onOpenChange={(o) => { if (!o) reset(); }}>
        <DialogContent data-testid="gallery-folder-consent-modal">
          <DialogHeader>
            <DialogTitle>
              {confirmStatus === "verified"
                ? "Confirm: mark folder consent verified"
                : confirmStatus === "unverified"
                ? "Confirm: mark folder consent unverified"
                : "Reset folder consent to unknown"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <div className="text-slate-700">
              You're about to set <strong>every asset</strong> in this folder to
              <code className="ml-1 px-1 py-0.5 bg-slate-100 rounded">{confirmStatus}</code>:
            </div>
            <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 break-all font-mono">
              {folder}
            </div>
            {confirmStatus === "verified" ? (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                <strong>This will flip photos from unsafe-pending-consent to safe.</strong>{" "}
                Only do this if you have documented consent from every identifiable
                person in this folder (signed releases, attendee disclaimer, etc.).
              </div>
            ) : null}
            <label className="block text-xs">
              <span className="font-medium text-slate-700">
                Reason <span className="text-rose-600">*</span> (required for the audit log)
              </span>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Internal HR meeting, attendee consent obtained at the door (Aug 2024 event)"
                rows={3}
                className="mt-1 text-xs"
                data-testid="gallery-folder-consent-reason"
                autoFocus
              />
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={reset}
              disabled={busy}
              data-testid="gallery-folder-consent-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy || reason.trim().length < 4}
              data-testid="gallery-folder-consent-submit"
            >
              {busy ? "Saving…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Filter panel ──────────────────────────────────────────────────────────
function FilterPanel({ filters, facets, onChange, onReset, workspaceId, onConsentChange }) {
  const toggleCsv = (key, value) => {
    const cur = new Set((filters[key] || "").split(",").filter(Boolean));
    if (cur.has(value)) cur.delete(value);
    else cur.add(value);
    onChange({ ...filters, [key]: Array.from(cur).join(","), page: 1 });
  };
  const set = (k, v) => onChange({ ...filters, [k]: v, page: 1 });
  const isSel = (key, v) => new Set((filters[key] || "").split(",").filter(Boolean)).has(v);

  return (
    <aside
      className="w-full lg:w-72 shrink-0 space-y-4 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto"
      data-testid="gallery-filter-panel"
    >
      <div className="flex items-center justify-between">
        <h2 className="font-semibold flex items-center gap-2">
          <FilterIcon className="h-4 w-4" /> Filters
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          data-testid="gallery-reset-filters"
        >
          Reset
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <SearchIcon className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search name / folder / tag…"
          className="pl-8"
          value={filters.q || ""}
          onChange={(e) => set("q", e.target.value)}
          data-testid="gallery-search-input"
        />
      </div>

      {/* Status */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
          Status
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            ["all", "All"],
            ["true", "Active"],
            ["false", "Inactive"],
          ].map(([val, label]) => (
            <Button
              key={val}
              variant={filters.is_active === val ? "default" : "outline"}
              size="sm"
              onClick={() => set("is_active", val)}
              data-testid={`gallery-filter-active-${val}`}
            >
              {label}
              {facets?.is_active_counts && val !== "all" ? (
                <span className="ml-1.5 text-xs opacity-70">
                  {facets.is_active_counts[val === "true" ? "True" : "False"] || 0}
                </span>
              ) : null}
            </Button>
          ))}
        </div>
      </div>

      {/* Phase 1.4 — Classifier verdict */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
          Classifier verdict
        </div>
        <div className="flex flex-wrap gap-2">
          {VERDICTS.map((v) => {
            const count = facets?.classifier_verdict_counts?.[v.key] || 0;
            return (
              <Button
                key={v.key}
                variant={isSel("classifier_verdict", v.key) ? "default" : "outline"}
                size="sm"
                onClick={() => toggleCsv("classifier_verdict", v.key)}
                data-testid={`gallery-filter-verdict-${v.key}`}
              >
                <span className={`inline-block h-2 w-2 rounded-full ${v.color} mr-1.5`} />
                {v.label}
                <span className="ml-1.5 text-xs opacity-70">{count}</span>
              </Button>
            );
          })}
        </div>
        {/* Classified vs unclassified short-cut */}
        <div className="flex flex-wrap gap-2 mt-2">
          {[
            ["all", "All"],
            ["classified", "Classified"],
            ["unclassified", "Unclassified"],
          ].map(([val, label]) => (
            <Button
              key={val}
              variant={(filters.classification_state || "all") === val ? "default" : "outline"}
              size="sm"
              onClick={() => set("classification_state", val === "all" ? "" : val)}
              data-testid={`gallery-filter-classification-state-${val}`}
            >
              {label}
              {val === "unclassified" && facets?.classifier_verdict_counts?.["(null)"] ? (
                <span className="ml-1.5 text-xs opacity-70">
                  {facets.classifier_verdict_counts["(null)"]}
                </span>
              ) : null}
            </Button>
          ))}
        </div>
      </div>

      {/* Phase 1.4 — Publication status */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
          Publication status
        </div>
        <div className="flex flex-wrap gap-2">
          {PUBLICATION_STATUSES.map((p) => {
            const count = facets?.publication_status_counts?.[p.key] || 0;
            return (
              <Button
                key={p.key}
                variant={isSel("publication_status", p.key) ? "default" : "outline"}
                size="sm"
                onClick={() => toggleCsv("publication_status", p.key)}
                data-testid={`gallery-filter-pubstatus-${p.key}`}
              >
                {p.label}
                <span className="ml-1.5 text-xs opacity-70">{count}</span>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Origin */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
          Origin
        </div>
        <div className="flex flex-wrap gap-2">
          {ORIGINS.map((o) => {
            const count = facets?.origin_counts?.[o] || 0;
            return (
              <Button
                key={o}
                variant={isSel("origin", o) ? "default" : "outline"}
                size="sm"
                onClick={() => toggleCsv("origin", o)}
                data-testid={`gallery-filter-origin-${o}`}
              >
                {o}
                <span className="ml-1.5 text-xs opacity-70">{count}</span>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Pool */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
          Pool
        </div>
        <div className="flex flex-wrap gap-2">
          {POOLS.map((p) => {
            const count = facets?.pool_counts?.[p] || 0;
            return (
              <Button
                key={p}
                variant={isSel("pool", p) ? "default" : "outline"}
                size="sm"
                onClick={() => toggleCsv("pool", p)}
                data-testid={`gallery-filter-pool-${p}`}
              >
                {p}
                <span className="ml-1.5 text-xs opacity-70">{count}</span>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Source folders */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wide text-slate-500">
            Source folder
          </div>
          {filters.source_folder ? (
            <FolderConsentButton
              workspaceId={workspaceId}
              folder={filters.source_folder}
              onChange={onConsentChange}
            />
          ) : null}
        </div>
        <div className="space-y-1 max-h-72 overflow-y-auto pr-1" data-testid="gallery-folder-list">
          <button
            className={`w-full text-left text-xs px-2 py-1.5 rounded border ${
              !filters.source_folder
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white hover:bg-slate-50 border-slate-200"
            }`}
            onClick={() => set("source_folder", "")}
            data-testid="gallery-folder-all"
          >
            All folders
          </button>
          {(facets?.source_folders || []).map((f) => (
            <button
              key={f.folder}
              className={`w-full text-left text-xs px-2 py-1.5 rounded border flex items-center justify-between ${
                filters.source_folder === f.folder
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white hover:bg-slate-50 border-slate-200"
              }`}
              onClick={() => set("source_folder", f.folder)}
              title={f.folder}
              data-testid={`gallery-folder-${(f.folder || "unknown").slice(-40)}`}
            >
              <span className="truncate">{shortFolder(f.folder)}</span>
              <span className="ml-2 opacity-70 shrink-0">{f.count}</span>
            </button>
          ))}
        </div>
      </div>
    </aside>
  );
}


export { FilterPanel, FolderConsentButton };
export default FilterPanel;
